// useSpaceLoader - laedt alle Spaces + Instanzen, reichert sie mit
// content-counts an und leitet die Space-Karten fuer Szene 0 ab.
//
// Datenmodell pro Space-Karte:
//   {
//     key, id, name, desc,
//     pakete, artefakte,
//     stages: [{ code, name, inst, instanceId, glyph, stageOrder }],
//     source, qas, prod, sourceInstance, qasInstance, prodInstance,
//     werkeList: [{key, name, kennung}],
//     usable, reason,
//   }
//
// Die Reihenfolge kommt aus utils/stages.js (DEV, QAS, PRE-PROD, PROD). Ein Space darf zwei,
// drei oder alle vier Stufen nutzen; fehlende werden uebersprungen. Nutzbar ist eine Schiene,
// sobald es eine Quellstufe und mindestens eine spaetere Stufe gibt.

import { useEffect, useState } from "react";
import { api } from "../../../utils/helpers";
import { loadAllInstances, parseInstanceWerke } from "../../../utils/transport-shared";
import { STAGES, naechsteStufe, stageOrderOf } from "../../../utils/stages";

const GLYPH_BY_STAGE = { dev: "ic-stage-dev", qas: "ic-stage-qas", preprod: "ic-stage-int", prod: "ic-stage-prod" };

function stageName(key) {
  const map = {
    dev: { de: "Entwicklung", en: "Development" },
    qas: { de: "Qualitätssicherung", en: "Quality Assurance" },
    preprod: { de: "Vorproduktion", en: "Pre-Production" },
    prod: { de: "Produktion", en: "Production" },
  };
  return map[key] || null;
}

// ⛔ Bewusst NUR ueber den Stufen-Namen, nie ueber stageOrder. Bis zum 31.07.2026 hiess
// stageOrder===2 "PROD"; seit PRE-PROD dazwischenliegt, waere das die Vorstufe - eine
// PRE-PROD-Instanz waere hier als Produktion gerendert worden.
function stageKeyOf(instance) {
  const s = String(instance?.stage || "").toLowerCase();
  return STAGES.some(x => x.key === s) ? s : null;
}

async function loadContentCountsSafe(instanceId) {
  try {
    const r = await api(`/api/instances/${instanceId}/content-counts`);
    return { packages: r?.packages ?? null, artifacts: r?.artifacts ?? null };
  } catch {
    return { packages: null, artifacts: null };
  }
}

export function useSpaceLoader(lang) {
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    loadAllInstances(api, { includeAll: true })
      .then(async (all) => {
        if (!mounted) return;
        const bySpace = new Map();
        for (const inst of all || []) {
          const key = inst.spaceId || inst.spaceName || "nospace";
          if (!bySpace.has(key)) bySpace.set(key, { spaceId: inst.spaceId, spaceName: inst.spaceName || "Ohne Space", insts: [] });
          bySpace.get(key).insts.push(inst);
        }
        const raw = [...bySpace.values()];
        // Content-Counts nur fuer den ersten Insert je Space fetch'n
        const cards = await Promise.all(raw.map(async (sp) => {
          // Welle 5 (V5.2, I5): Eine nicht eingerichtete Zusatzinstanz (cicdPending) ist kein Transportziel und bleibt aus
          // der Landschaft heraus, wie im Backend (Sicht ohne Stufen-Tag). Ohne das Feld bleibt alles wie bisher.
          const tagged = sp.insts
            .map((i) => ({ inst: i, key: i.cicdPending ? null : stageKeyOf(i) }))
            .filter((x) => x.key);
          tagged.sort((a, b) => (stageOrderOf(a.key) ?? 9) - (stageOrderOf(b.key) ?? 9));
          const vorhandene = [...new Set(tagged.map((x) => x.key))];
          const dev = tagged.find((x) => x.key === "dev")?.inst || null;
          const prod = tagged.find((x) => x.key === "prod")?.inst || null;
          // Quelle ist die erste vorhandene Stufe, Ziel die naechste dahinter - nicht mehr fest
          // DEV und QAS. Ein Space mit DEV + PRE-PROD + PROD transportiert von DEV nach PRE-PROD.
          const quelleKey = vorhandene[0] || null;
          const zielKey = quelleKey ? naechsteStufe(vorhandene, quelleKey) : null;
          const quelle = quelleKey ? tagged.find((x) => x.key === quelleKey)?.inst || null : null;
          const ziel = zielKey ? tagged.find((x) => x.key === zielKey)?.inst || null : null;
          // Je Stufe genau ein Eintrag. Hat eine Stufe mehrere Instanzen (Mehrzielmodus), bleibt `instanceId` leer
          // und `candidates[]` nennt die Instanzen; das Standardziel bestimmt erst die Pipeline-Übersicht.
          const stages = vorhandene.map((key) => {
            const list = tagged.filter((x) => x.key === key).map((x) => x.inst);
            const first = list[0];
            const nm = stageName(key);
            return {
              code: STAGES.find((s) => s.key === key)?.label || key.toUpperCase(),
              stageKey: key,
              name: nm ? (nm[lang || "de"] || nm.de) : first.name,
              inst: first.name,
              instanceId: list.length > 1 ? null : first.id,
              ...(list.length > 1 ? { candidates: list } : {}),
              glyph: GLYPH_BY_STAGE[key] || "ic-stage-dev",
              stageOrder: first.stageOrder,
            };
          });
          // Werke haengen an der ZIELinstanz, nicht mehr fest an QAS.
          const werkeList = ziel ? parseInstanceWerke(ziel).map((w) => ({ key: w.kennung, name: w.name, kennung: w.kennung })) : [];
          // Nutzbar, sobald zwei aufeinanderfolgende Stufen existieren. Vorher galt zwingend
          // DEV UND QAS - ein Space mit DEV+PROD war damit gesperrt, obwohl er transportfaehig ist.
          const usable = !!(quelle && ziel);
          // reason ist ein i18n-Key, wird in Scene0 via t() aufgeloest
          const reason = !quelle ? "s0.noStage" : !ziel ? "s0.noTarget" : null;
          // content-counts summieren
          const counts = await Promise.all(sp.insts.slice(0, 3).map((i) => loadContentCountsSafe(i.id)));
          const paketeAll = counts.map((c) => c.packages).filter((n) => typeof n === "number");
          const artefakteAll = counts.map((c) => c.artifacts).filter((n) => typeof n === "number");
          const pakete = paketeAll.length ? Math.max(...paketeAll) : null;
          const artefakte = artefakteAll.length ? Math.max(...artefakteAll) : null;
          return {
            key: sp.spaceId || sp.spaceName,
            id: sp.spaceId,
            name: sp.spaceName,
            desc: null,
            pakete,
            artefakte,
            stages,
            sourceInstance: quelle,
            // qasInstance heisst aus Kompatibilitaet weiter so, ist aber die naechste Stufe der
            // Schiene - bei einem Space ohne QAS also z. B. die PRE-PROD-Instanz.
            qasInstance: ziel,
            prodInstance: prod,
            devInstance: dev,
            stufen: vorhandene,
            quellStufe: quelleKey,
            zielStufe: zielKey,
            werkeList,
            usable,
            reason,
          };
        }));
        cards.sort((a, b) => a.name.localeCompare(b.name));
        setSpaces(cards);
        setLoading(false);
      })
      .catch((e) => {
        if (!mounted) return;
        setError(e?.message || "Instanzen konnten nicht geladen werden");
        setLoading(false);
      });
    return () => { mounted = false; };
  }, [lang]);

  return { spaces, loading, error, reload: () => setLoading(true) };
}
